import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import type * as core from '../../core'

export enum AssetType {
  Building = 'Building',
  FixedEquipment = 'Fixed-Equipment',
  MovableEquipment = 'Movable-Equipment',
}
// Needs to be before namespace is defined
const ASSET_TYPES: ReadonlySet<AssetType> = new Set(
  Object.values(AssetType) as AssetType[],
)
export namespace AssetType {
  export const ALL: readonly AssetType[] = Array.from(ASSET_TYPES)
  export const isAssetType = TypeChecker.Factory.oneOf(ASSET_TYPES)
  export const assertIsAssetType: TypeAsserter<AssetType> =
    TypeAsserter.Factory.from(isAssetType)

  /**
   * Returns the {@link core.common.value.types.Enum.Entry} for the given
   * {@link AssetType}
   */
  export function getEntry(
    assetType: AssetType,
  ): core.common.value.types.Enum.Entry {
    return { ...ENTRY_LOOKUP[assetType], id: assetType }
  }
  /** Used by {@link getEntry} */
  const ENTRY_LOOKUP: Record<
    AssetType,
    Omit<core.common.value.types.Enum.Entry, 'id'>
  > = {
    [AssetType.Building]: {
      description: 'A building or other real estate structure',
      name: 'Building',
    },
    [AssetType.FixedEquipment]: {
      description: 'Equipment that is structurally attached to a building',
      name: 'Fixed Equipment',
    },
    [AssetType.MovableEquipment]: {
      description:
        'Equipment that is not structurally attached to any building',
      name: 'Movable Equipment',
    },
  }

  const FOR_EQUIPMENT = [
    AssetType.FixedEquipment,
    AssetType.MovableEquipment,
  ] as const
  export type ForEquipment = (typeof FOR_EQUIPMENT)[number]
  export namespace ForEquipment {
    export const isForEquipment = TypeChecker.Factory.oneOf(FOR_EQUIPMENT)
    export const assertIsForEquipment: TypeAsserter<ForEquipment> =
      TypeAsserter.Factory.from(isForEquipment)

    // eslint-disable-next-line @typescript-eslint/no-shadow
    export const ALL: readonly AssetType[] =
      AssetType.ALL.filter(isForEquipment)
  }
}
